import FileTextIcon from '@src/svgs/quick-actions/FileTextIcon';
import MessageSquareIcon from '@src/svgs/quick-actions/MessageSquareIcon';
import QuoteIcon from '@src/svgs/quick-actions/QuoteIcon';
import VideoIcon from '@src/svgs/quick-actions/VideoIcon';
import styled from 'styled-components';

import type { ContentItem, ContentItemKey } from './contentSummary';
import type { FC } from 'react';

const iconFor = (key: ContentItemKey) => {
  switch (key) {
    case 'clips':
      return <VideoIcon />;
    case 'quotes':
      return <QuoteIcon />;
    case 'articles':
      return <FileTextIcon />;
    case 'social':
      return <MessageSquareIcon />;
  }
};

type Props = {
  items: ContentItem[];
};

/**
 * The Story Manager "Content" cell — generated-output counts (clips, quotes,
 * articles, social) as icon + label items laid out two per line. Renders an
 * em-dash when nothing has been generated.
 *
 * An item may carry a `detail` sub-line (clips: how many are published). It
 * hangs under its own label, indented past the icon, and leaves the two-per-line
 * layout alone — siblings keep their column and stay on the first line.
 */
const ContentCell: FC<Props> = ({ items }) => {
  if (items.length === 0) {
    return <Empty>—</Empty>;
  }

  return (
    <Grid>
      {items.map(item => (
        <Item key={item.key}>
          <LabelRow>
            {iconFor(item.key)}
            <span>{item.label}</span>
          </LabelRow>
          {item.detail && <Detail>{item.detail}</Detail>}
        </Item>
      ))}
    </Grid>
  );
};

export default ContentCell;

// Width mirrors the header and the clip row's aspect-badge column so all rows
// line up; two equal columns lay the items out two per line.
const Grid = styled.div`
  width: 180px;
  flex-shrink: 0;
  display: grid;
  grid-template-columns: auto auto;
  justify-content: start;
  gap: 2px 20px;
  align-content: center;
  /* Items top-align within their row, so a sub-line under one count can't drag
     a sibling count down off the first line. */
  align-items: start;
`;

const Empty = styled.span`
  width: 180px;
  flex-shrink: 0;
  font-size: var(--text-xs);
  color: var(--text-muted);
`;

const Item = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  color: var(--text-secondary);
`;

// The icon belongs to the label, so it sits on the label's line rather than
// centring itself against a two-line block.
const LabelRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;

  svg {
    width: 14px;
    height: 14px;
    color: var(--text-muted);
    flex-shrink: 0;
  }
`;

// Hangs under the label text, not under the icon (14px icon + 4px gap). A step
// smaller and dimmer than the count — 11px is this table's meta size
// (`StoryTopicsCell`, the row's own action buttons) — so the cell reads as one
// count with a note, not two competing lines.
const Detail = styled.span`
  padding-left: 18px;
  font-size: 11px;
  line-height: 1.4;
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
